import Pusher, { Channel, PresenceChannel } from 'pusher-js';
import { CLASS_PRESENCE_CHANNEL_PREFIX } from 'constants/class-management.constant';
import { PRIVATE_USER_CHANNEL_PREFIX } from 'constants/class-session.constant';
import { PROJECT_NOTE } from 'constants/project-note';
import Endpoints from 'constants/endpoints';
import type { ProjectNoteChannelParams } from 'interfaces/project-note.interface';
import focusBearApi from 'services/axios-config';
import { captureException } from 'services/posthog.service';

let pusherClient: Pusher | null = null;

/** noteId → projectId for note-channel auth (channel names omit projectId). */
const noteProjectIdByNoteId = new Map<string, string>();

type ChannelAuthCallback = (
  error: Error | null,
  authData: { auth: string; channel_data?: string } | null
) => void;

const getPusherEnvConfig = (): { appKey: string; cluster: string } | null => {
  const appKey = process.env.REACT_APP_PUSHER_APP_KEY ?? '';
  const cluster = process.env.REACT_APP_PUSHER_CLUSTER ?? '';
  if (appKey.length === 0 || cluster.length === 0) {
    return null;
  }
  return { appKey, cluster };
};

/** Env check only — does not construct the Pusher singleton. */
export const isPusherConfigured = (): boolean => getPusherEnvConfig() !== null;

/**
 * Lazy-init Pusher singleton.
 *
 * Channel authorization goes through `focusBearApi` (rather than pusher-js's
 * default form-encoded `authEndpoint`) so the bearer token and the axios
 * interceptors (refresh, rate-limit handling) ride along with the request.
 *
 * Auth is routed by channel prefix (BE has separate endpoints):
 * - `private-*` → `PUSHER_USER_AUTH` (`{ socket_id }` only; signs caller's channel)
 * - `presence-class-*` → `PUSHER_CHANNEL_AUTH` (teachers / class admins only)
 * - `presence-project-note-*` (incl. `presence-project-note-private-*`) → note
 *   realtime-auth (needs registered projectId)
 *
 * Students must not hit channel-auth for private channels — BE returns 400.
 *
 * Returns null when the Pusher env vars are not configured so callers can
 * degrade gracefully (no live updates; the initial fetch on load still runs).
 */
export const getPusherClient = (): Pusher | null => {
  const config = getPusherEnvConfig();
  if (config == null) {
    return null;
  }

  if (!pusherClient) {
    pusherClient = new Pusher(config.appKey, {
      cluster: config.cluster,
      channelAuthorization: {
        customHandler: ({ socketId, channelName }, callback) => {
          authorizePusherChannel(socketId, channelName, callback);
        }
      }
    });
  }

  return pusherClient;
};

const authorizePusherChannel = (
  socketId: string,
  channelName: string,
  callback: ChannelAuthCallback
): void => {
  if (channelName.startsWith(PRIVATE_USER_CHANNEL_PREFIX)) {
    authorizePrivateUserChannel(socketId, callback);
    return;
  }
  if (channelName.startsWith(CLASS_PRESENCE_CHANNEL_PREFIX)) {
    authorizeClassPresenceChannel(socketId, channelName, callback);
    return;
  }
  if (channelName.startsWith(PROJECT_NOTE.REALTIME.PRESENCE_CHANNEL_PREFIX)) {
    authorizeProjectNotePresenceChannel(socketId, channelName, callback);
    return;
  }
  const error = new Error(`Unsupported Pusher channel: ${channelName}`);
  captureException(error);
  callback(error, null);
};

const authorizePrivateUserChannel = (
  socketId: string,
  callback: ChannelAuthCallback
): void => {
  focusBearApi
    .post(Endpoints.PUSHER_USER_AUTH, { socket_id: socketId })
    .then(({ data }) => callback(null, data))
    .catch((error) => {
      captureException(error);
      callback(error as Error, null);
    });
};

const authorizeClassPresenceChannel = (
  socketId: string,
  channelName: string,
  callback: ChannelAuthCallback
): void => {
  focusBearApi
    .post(Endpoints.PUSHER_CHANNEL_AUTH, {
      socket_id: socketId,
      channel_name: channelName
    })
    .then(({ data }) => callback(null, data))
    .catch((error) => {
      captureException(error);
      callback(error as Error, null);
    });
};

const authorizeProjectNotePresenceChannel = (
  socketId: string,
  channelName: string,
  callback: ChannelAuthCallback
): void => {
  const noteId = getNoteIdFromProjectNoteChannelName(channelName);
  const projectId = noteProjectIdByNoteId.get(noteId);
  if (projectId == null || projectId.length === 0 || noteId.length === 0) {
    const error = new Error(
      `Missing project context for Pusher note channel: ${channelName}`
    );
    captureException(error);
    callback(error, null);
    return;
  }
  focusBearApi
    .post(Endpoints.PROJECT_NOTE_REALTIME_AUTH(projectId, noteId), {
      socket_id: socketId
    })
    .then(({ data }) => callback(null, data))
    .catch((error) => {
      captureException(error);
      callback(error as Error, null);
    });
};

export const getPresenceClassChannelName = (teamId: string) =>
  `${CLASS_PRESENCE_CHANNEL_PREFIX}${teamId}`;

/** Teacher live board only — students are rejected by `/pusher/channel-auth`. */
export const subscribeToPresenceClassChannel = (
  teamId: string
): PresenceChannel | null => {
  const client = getPusherClient();

  if (!client) {
    return null;
  }

  return client.subscribe(
    getPresenceClassChannelName(teamId)
  ) as PresenceChannel;
};

export const unsubscribeFromPresenceClassChannel = (teamId: string) => {
  pusherClient?.unsubscribe(getPresenceClassChannelName(teamId));
};

export const getPrivateUserChannelName = (userId: string) =>
  `${PRIVATE_USER_CHANNEL_PREFIX}${userId}`;

/** Student / app private bus (desktop + CLASS TodoPlayer checklist sync). */
export const subscribeToPrivateUserChannel = (
  userId: string
): Channel | null => {
  const client = getPusherClient();
  if (!client) {
    return null;
  }
  return client.subscribe(getPrivateUserChannelName(userId));
};

export const unsubscribeFromPrivateUserChannel = (userId: string) => {
  pusherClient?.unsubscribe(getPrivateUserChannelName(userId));
};

// Must match the BE's `noteRealtimeChannel`: it only signs the channel for the note's current privacy.
export const getPresenceProjectNoteChannelName = (
  noteId: string,
  isPrivate: boolean
): string =>
  `${
    isPrivate
      ? PROJECT_NOTE.REALTIME.PRIVATE_PRESENCE_CHANNEL_PREFIX
      : PROJECT_NOTE.REALTIME.PRESENCE_CHANNEL_PREFIX
  }${noteId}`;

export const getNoteIdFromProjectNoteChannelName = (
  channelName: string
): string => {
  const { PRESENCE_CHANNEL_PREFIX, PRIVATE_PRESENCE_CHANNEL_PREFIX } =
    PROJECT_NOTE.REALTIME;
  const prefix = channelName.startsWith(PRIVATE_PRESENCE_CHANNEL_PREFIX)
    ? PRIVATE_PRESENCE_CHANNEL_PREFIX
    : PRESENCE_CHANNEL_PREFIX;
  return channelName.slice(prefix.length);
};

export const registerProjectNoteChannelContext = (
  noteId: string,
  projectId: string
): void => {
  noteProjectIdByNoteId.set(noteId, projectId);
};

export const clearProjectNoteChannelContext = (noteId: string): void => {
  noteProjectIdByNoteId.delete(noteId);
};

export const subscribeToPresenceProjectNoteChannel = ({
  projectId,
  noteId,
  isPrivate
}: ProjectNoteChannelParams): PresenceChannel | null => {
  const client = getPusherClient();
  if (client == null) {
    return null;
  }
  registerProjectNoteChannelContext(noteId, projectId);
  return client.subscribe(
    getPresenceProjectNoteChannelName(noteId, isPrivate)
  ) as PresenceChannel;
};

export const unsubscribeFromPresenceProjectNoteChannel = (
  noteId: string,
  isPrivate: boolean
): void => {
  pusherClient?.unsubscribe(getPresenceProjectNoteChannelName(noteId, isPrivate));
  clearProjectNoteChannelContext(noteId);
};

export const disconnectPusherClient = () => {
  if (pusherClient) {
    pusherClient.disconnect();
    pusherClient = null;
  }
  noteProjectIdByNoteId.clear();
};
